// Single row in a citizen's constraint table. Renders as read or add depending on
// props — citizen constraints have no edit capability (only add/delete), unlike
// employee and category constraints.
import { useState } from "react";
import { Constraint } from "../../types/constraint";
import labels from "../../utils/labels";
import constraintStyle from "../../styles/constraint.module.css";
import { ReactComponent as DeleteIcon } from "../../assets/icons/bin.svg";
import IconButtonComponent from "../UI/buttons/IconButton";
import DeleteConstraintModal from "./DeleteConstraintModal";
import AddConstraintSearch from "./AddConstraintSearch";
import StandardButton from "../UI/buttons/StandardButton";
import { useAppDispatch } from "../../store/hooks";
import ConstraintService from "../../service/constraintService";
import { getTakenCategoryIdsForCitizen } from "../../service/constraintDuplicates";
import { addConstraintToClient } from "../../store/constraint";

const CitizenConstraintRow = ({
  constraint,
  clientId,
  existingConstraints,
  onDelete,
  onAddCancel,
}: {
  // Present for an existing constraint (read); absent for the add row.
  constraint?: Constraint;
  clientId: number;
  existingConstraints?: Constraint[];
  onDelete?: () => void;
  onAddCancel?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTargetId, setSelectedTargetId] = useState<number | undefined>(
    undefined,
  );
  const [isPending, setIsPending] = useState(false);

  const isAdding = constraint === undefined;
  const excludedTargetIds = getTakenCategoryIdsForCitizen(existingConstraints);

  const handleSave = async () => {
    if (selectedTargetId === undefined) return;
    setIsPending(true);
    try {
      const created = await ConstraintService.addClientConstraint(
        { serviceClientId: clientId, targetId: selectedTargetId },
        dispatch,
      );
      dispatch(addConstraintToClient({ clientId, constraint: created }));
      onAddCancel?.();
    } catch {
      // error notification handled in service
    }
    setIsPending(false);
  };

  return (
    <div
      className={`${constraintStyle.citizenTableRow} ${isAdding ? constraintStyle.staticRow : ""}`}
      style={{ paddingLeft: "var(--spacing-lg)" }}
    >
      {isAdding ? (
        <AddConstraintSearch
          isCategoryConstraint
          onSelect={(id) => setSelectedTargetId(id)}
          excludedTargetIds={excludedTargetIds}
        />
      ) : (
        <p>{constraint.targetName}</p>
      )}

      {isAdding ? (
        <div
          className={`${constraintStyle.citizenFormActions} flex justify-between gap-[var(--spacing-base)] items-center`}
        >
          <StandardButton
            variant="TERTIARY"
            onClick={() => onAddCancel?.()}
            ariaLabel={labels.cancel}
          >
            {labels.cancel}
          </StandardButton>
          <StandardButton
            variant="PRIMARY"
            onClick={handleSave}
            ariaLabel={labels.save}
            disable={selectedTargetId === undefined}
            pending={isPending}
          >
            {labels.save}
          </StandardButton>
        </div>
      ) : (
        <div className={constraintStyle.actionColumn}>
          <IconButtonComponent
            id={`delete-citizen-constraint-${constraint.id}`}
            icon={<DeleteIcon width={24} height={24} />}
            onClick={() => setShowDeleteModal(true)}
            ariaLabel={labels.deleteConstraint}
          />
        </div>
      )}

      {showDeleteModal && (
        <DeleteConstraintModal
          onDelete={() => {
            onDelete?.();
            setShowDeleteModal(false);
          }}
          onDiscard={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default CitizenConstraintRow;
